import Api from '../Api';
import sortBy from 'lodash/sortBy';
import partition from 'lodash/partition';
import { heatLossCalculator, OPERATION_HEAT, productAnalyser } from './calculators';

const MAX_RESULTS = 5

let productCache = [];

const fetchProducts = async () => {
    if (productCache.length) {
        return productCache;
    }

    const { data } = await Api.getProducts()

    productCache = data;

    return productCache;
}

const ProductRecommender = async function (pool) {
    const heatLoss = heatLossCalculator({
        surfaceAreaInMeters: pool.surface_area,
        constructionType: pool.construction_type,
        requiredPoolTempInC: pool.required_water_temperature,
        altitudeAboveSeaLevelInMeters: pool.altitude,
        airDryBulbTempInC: pool.air_temperature,
        relativeHumidityAsPercentage: pool.humidity,
        hoursOfSun: pool.sunshine_hours,
        windSpeedInKph: pool.wind_speed,
        groundTempInC: pool.ground_temperature,
        waterPumpKw: pool.water_pump_kw,
        sunIntensity: pool.sun_intensity,
        poolUsage: pool.pool_usage,
        pumpRunningHours: pool.pump_running_hours,
        numberOfPeople: pool.number_of_people,
        hoursUsed: pool.hours_used_a_day,
        hoursUncovered: pool.hours_left_uncovered,
        waterCharacteristics: pool.water_characteristics,
        waterTableHeight: pool.water_table_height,
        safetyMargin: pool.safety_margin,
    })

    if (heatLoss.netHeatLoss <= 0) {
        return {
            heatLoss,
            products: [],
        }
    }

    const productAnalysis = productAnalyser({
        poolLoss: heatLoss.netHeatLoss,
        safetyMargin: pool.safety_margin,
        products: await fetchProducts(),
        operation: OPERATION_HEAT,
        hotWater: false,
        powerSupply: 50,
        relativeHumidityAsPercentage: pool.humidity,
        airDryBulbTempInC: pool.air_temperature,
        requiredPoolTempInC: pool.required_water_temperature,
        pumpRunningHours: pool.pump_running_hours,
    })

    const filteredProducts = filterResults(productAnalysis, pool.power_supply, pool.air_temperature)
    const orderedProducts = orderProducts(filteredProducts)
    const products = truncateResults(orderedProducts)

    return {
        heatLoss,
        products,
    }
}

const filteredSeries = (ordered, unique) => {
    return Array.from(new Set(ordered)).filter(element => {
        const isDuplicate = unique.includes(element.model.meta.series[0].id);

        if (!isDuplicate) {
            unique.push(element.model.meta.series[0].id);

            return true;
        }
    });
}

const orderedOthers = (ordered, filteredSeries) => {
    return Array.from(new Set(ordered)).filter(product => !filteredSeries.includes(product));
}

const orderProducts = products => {
    let uniqueSeries = [],
        uniqueSeriesMulti = [];

    // Partition products into 2 arrays - ones that need 1 unit and one that requires more
    const [onlyNeedOne, needMoreThanOne] = partition(products, result => result.analysis.unitsRequired === 1)

    // Order our partitioned arrays by heat duty - multiples would require units required first
    const orderedOnes = sortBy(onlyNeedOne, ['analysis.heatDuty'])
    const orderedMultiples = sortBy(needMoreThanOne, ['analysis.unitsRequired', 'analysis.heatDuty'])

    // Filter our results to supply the lowest heat duty for each series
    const oneSeries = filteredSeries(orderedOnes, uniqueSeries);
    const oneSeriesOrderedMultiples = filteredSeries(orderedMultiples, uniqueSeriesMulti);

    // Return our results, 1st single units by product series order in the CMS, then the lowest heat duty not in previous list, then if more than 1 repeat but for multiples
    return []
        .concat(sortBy(oneSeries, ['model.meta.order']))
        .concat(orderedOthers(orderedOnes, oneSeries))
        .concat(sortBy(oneSeriesOrderedMultiples, ['model.meta.order']))
        .concat(orderedOthers(orderedMultiples, oneSeriesOrderedMultiples))
}

const filterResults = (allModels, powerSupplyType, minimumTemperature) => {
    return allModels
        .filter(result => {
            if (powerSupplyType === 'Both') {
                return true
            }

            return result.model.maths.powerSupplyType === powerSupplyType
        })
        .filter(result => {
            return minimumTemperature >= result.model.maths.minimumOperatingTemperature;
        })
}

const truncateResults = products => {
    if (products.length > MAX_RESULTS) {
        return products.slice(0, MAX_RESULTS);
    }

    return products;
}

export default ProductRecommender
